import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FiSend, FiGlobe, FiHash, FiZap, FiCheckCircle } from "react-icons/fi";
import { postRequestAuthenticated } from "../../../services/rest";
import { fetchbusinessCredentials } from "../../../redux/slices/businessSlice";
import ConfirmDialog from "../../common/ConfirmDialog";
import { toast } from "../../../utils/toast";
import { draftGuard } from "../../../utils/draftGuard";

const MAX_CHANGES = 10;

// channelssocial.com brand palette — rotated across channel cards for a playful,
// "your people, your space" feel.
const PALETTE = ["#FBB28B", "#96D7FF", "#B2EE8F", "#E0AAFF"];
const colorFor = (i) => PALETTE[i % PALETTE.length];

const SUGGESTIONS = [
  "Add a topic for events",
  "Make the names more casual",
  "Add a support channel",
  "Rename a topic",
];

// A root-level channel and the recommended topics inside it.
const ChannelCard = ({ ch, i }) => (
  <div className="rounded-xl border border-theme-chatDivider bg-theme-primaryBackground px-3 py-2.5 transition-transform duration-150 hover:-translate-y-0.5">
    <div className="flex items-start gap-2.5">
      <span
        className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: colorFor(i) }}
      >
        <FiHash className="text-[#202020]" size={13} />
      </span>
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-theme-secondaryText">
          {ch.name}
        </div>
        <div className="text-xs text-theme-placeholder">{ch.description}</div>
      </div>
    </div>
    {ch.topics && ch.topics.length > 0 && (
      <div className="mt-2 flex flex-wrap gap-1.5 pl-8">
        {ch.topics.map((t, k) => (
          <span
            key={k}
            title={t.description || ""}
            className="rounded-full border border-theme-chatDivider bg-theme-secondaryBackground px-2 py-0.5 text-[11px] font-medium text-theme-secondaryText"
          >
            {t.name}
          </span>
        ))}
      </div>
    )}
  </div>
);

// Small gradient bot avatar for assistant turns.
const BotAvatar = () => (
  <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#FBB28B] via-[#96D7FF] to-[#B2EE8F] shadow-sm">
    <FiZap size={13} className="text-[#202020]" />
  </span>
);

const Row = ({ role, children }) => {
  const mine = role === "user";
  return (
    <div className={`flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`}>
      {!mine && <BotAvatar />}
      <div
        className={`max-w-[82%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
          mine
            ? "rounded-br-sm bg-theme-accent text-theme-accentText"
            : "rounded-bl-sm bg-theme-secondaryBackground text-theme-secondaryText"
        }`}
      >
        {children}
      </div>
    </div>
  );
};

// AI setup assistant: scrape -> propose channels -> refine by chat -> commit.
// autoStart (add-more mode): the business already gave its website during the
// original setup — skip the URL ask and draft immediately. If there's somehow
// no stored website, we still fall back to asking.
const BuildCommunityStep = ({ onDone, autoStart = false }) => {
  const dispatch = useDispatch();
  const business = useSelector((s) => s.business.business);

  // Only auto-draft if the stored website actually looks like a URL — a junk
  // value (e.g. "hi") would just fire a doomed scrape, so fall back to asking.
  const looksLikeUrl = (s) => /\.[a-z]{2,}/i.test((s || "").trim());
  const skipIntro = autoStart && looksLikeUrl(business?.website);
  const [phase, setPhase] = useState("intro"); // intro | ready | done
  // In add-more mode with a junk stored site, don't prefill it into the box.
  const [url, setUrl] = useState(
    skipIntro || !autoStart ? business?.website || "" : ""
  );
  const [messages, setMessages] = useState([
    skipIntro
      ? {
          role: "assistant",
          text:
            "Let's add to your community ✨ I'm drafting some fresh channel ideas from your site — one sec…",
        }
      : autoStart
      ? {
          role: "assistant",
          text:
            "Let's add more channels ✨ Drop your website and I'll draft some fresh ideas that fit alongside what you've already got.",
        }
      : {
          role: "assistant",
          text:
            "Hey! I'm your setup assistant ✨ Drop in your website and I'll spin up a starter community — a couple of channels with a few topics inside each — then just tell me what to tweak.",
        },
  ]);
  const [channels, setChannels] = useState([]);
  const [changesLeft, setChangesLeft] = useState(MAX_CHANGES);
  const [input, setInput] = useState("");
  // In skip-intro mode we start "busy" so the typing indicator shows from the
  // first frame (the auto-draft fires on mount).
  const [busy, setBusy] = useState(skipIntro);
  const [confirmCommit, setConfirmCommit] = useState(false);

  const topicTotal = channels.reduce(
    (n, c) => n + (c.topics ? c.topics.length : 0),
    0
  );

  const scrollRef = useRef(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, busy]);

  // A draft that's been generated ("ready") but not yet created is unsaved
  // work. Flag it so navigation can warn before discarding it: the shared
  // draftGuard covers in-app tab switches (checked in AdminHome), and a
  // beforeunload handler covers real page close / refresh.
  const dirty = phase === "ready";
  useEffect(() => {
    draftGuard.set(dirty);
    return () => draftGuard.set(false);
  }, [dirty]);
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const push = (m) => setMessages((prev) => [...prev, m]);

  // Add-more mode: kick off the draft immediately with the stored website
  // (guarded so StrictMode's double mount doesn't fire it twice).
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (skipIntro && !autoStartedRef.current) {
      autoStartedRef.current = true;
      generate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    let site = url.trim();
    if (!site) {
      toast("Enter your website URL.", "error");
      return;
    }
    // Accept "acme.com" too — default to https:// when no scheme is given.
    if (!/^https?:\/\//i.test(site)) site = "https://" + site;
    setBusy(true);
    push({ role: "user", text: `Set up a community for ${site}` });
    try {
      const res = await postRequestAuthenticated("/setup/draft", { url: site });
      if (res?.success) {
        setChannels(res.channels || []);
        setChangesLeft(res.changesLeft ?? MAX_CHANGES);
        setPhase("ready");
        push({
          role: "assistant",
          text:
            "Here's a starter community based on your site 🎉 A couple of channels, each with a few topics inside. Tell me what to change — add, rename or remove channels and topics — or create it as-is.",
          channels: res.channels || [],
        });
      } else if (res?.upgrade) {
        setPhase("upgrade");
        push({
          role: "assistant",
          text:
            res.message ||
            "The AI setup assistant is available on the Growth plan.",
        });
      } else {
        push({
          role: "assistant",
          text: res?.message || "Couldn't generate channels. Try again.",
        });
      }
    } catch (e) {
      push({
        role: "assistant",
        text: "Something went wrong reaching the assistant. Try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  const refine = async (preset) => {
    const msg = (preset ?? input).trim();
    if (!msg || busy) return;
    if (changesLeft <= 0) {
      toast("You've used all your changes — create the community to continue.", "error");
      return;
    }
    setInput("");
    push({ role: "user", text: msg });
    setBusy(true);
    try {
      const res = await postRequestAuthenticated("/setup/refine", { message: msg });
      if (res?.success) {
        setChannels(res.channels || []);
        setChangesLeft(res.changesLeft ?? changesLeft - 1);
        push({
          role: "assistant",
          text: "Updated — here's how your community looks now.",
          channels: res.channels || [],
        });
      } else {
        if (res?.limitReached) setChangesLeft(0);
        push({
          role: "assistant",
          text: res?.message || "Couldn't apply that change.",
        });
      }
    } catch (e) {
      push({ role: "assistant", text: "Something went wrong. Try again." });
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setConfirmCommit(false);
    setBusy(true);
    try {
      const res = await postRequestAuthenticated("/setup/commit", {});
      const createdCount = res?.created?.length || 0;
      if (res?.success && createdCount > 0) {
        setPhase("done");
        push({
          role: "assistant",
          text:
            (res.message || `Created ${createdCount} channel(s).`) +
            " Your channels and topics are ready to go. 🚀",
        });
        dispatch(fetchbusinessCredentials());
      } else if (res?.success) {
        // Succeeded but created nothing (e.g. plan limit skipped them all) —
        // stay on this step and surface why instead of a false "done".
        push({
          role: "assistant",
          text:
            res.message ||
            "No channels were created — you may have reached your plan's channel limit.",
        });
      } else {
        push({
          role: "assistant",
          text: res?.message || "Couldn't create the community.",
        });
      }
    } catch (e) {
      push({
        role: "assistant",
        text: "Something went wrong creating the community.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col">
      {/* funky gradient hero */}
      <div className="relative mb-3 overflow-hidden rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-4">
        <div className="pointer-events-none absolute -right-10 -top-12 h-36 w-36 rounded-full bg-[#96D7FF] opacity-25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-12 left-8 h-32 w-32 rounded-full bg-[#FBB28B] opacity-25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-10 right-16 h-28 w-28 rounded-full bg-[#B2EE8F] opacity-20 blur-3xl" />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-[#FBB28B] via-[#96D7FF] to-[#B2EE8F] shadow-md">
              <FiZap size={20} className="text-[#202020]" />
            </span>
            <div>
              <h2 className="bg-gradient-to-r from-[#FBB28B] via-[#96D7FF] to-[#B2EE8F] bg-clip-text text-lg font-bold text-transparent">
                AI setup assistant
              </h2>
              <p className="text-xs text-theme-placeholder">
                Spin up your community's channels in a few messages.
              </p>
            </div>
          </div>
          {phase === "ready" && (
            <span className="flex-shrink-0 rounded-full border border-theme-chatDivider bg-theme-primaryBackground px-2.5 py-1 text-xs font-medium text-theme-placeholder">
              {changesLeft} left
            </span>
          )}
        </div>
      </div>

      {/* conversation */}
      <div
        ref={scrollRef}
        className="relative flex-1 space-y-3 overflow-y-auto rounded-2xl border border-theme-chatDivider bg-theme-tertiaryBackground p-4 custom-scrollbar"
      >
        {messages.map((m, i) => (
          <div key={i} className="space-y-2">
            <Row role={m.role}>{m.text}</Row>
            {m.channels && m.channels.length > 0 && (
              <div className="ml-9 grid gap-2">
                {m.channels.map((ch, j) => (
                  <ChannelCard key={j} ch={ch} i={j} />
                ))}
              </div>
            )}
          </div>
        ))}
        {busy && (
          <Row role="assistant">
            <span className="inline-flex gap-1 py-0.5">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-theme-placeholder [animation-delay:-0.2s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-theme-placeholder [animation-delay:-0.1s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-theme-placeholder" />
            </span>
          </Row>
        )}
      </div>

      {/* composer */}
      <div className="mt-3 pb-6 sm:pb-8">
        {/* Hidden while the add-more auto-draft runs; if it fails, the box
            reappears (prefilled) so they can retry or fix the URL. */}
        {phase === "intro" && !(skipIntro && busy) && (
          <div className="flex items-center gap-2 rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground px-3 focus-within:border-[#96D7FF]">
            <FiGlobe className="text-theme-placeholder" size={16} />
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !busy && generate()}
              placeholder="yoursite.com"
              disabled={busy}
              className="w-full bg-transparent py-3 text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder"
            />
            <button
              onClick={generate}
              disabled={busy}
              className="my-1.5 flex-shrink-0 rounded-xl bg-gradient-to-r from-[#96D7FF] to-[#B2EE8F] px-4 py-2 text-sm font-bold text-[#202020] shadow-sm transition-transform hover:-translate-y-0.5 disabled:translate-y-0 disabled:opacity-50"
            >
              {busy ? "Generating…" : "✨ Generate"}
            </button>
          </div>
        )}

        {phase === "ready" && (
          <div className="space-y-2.5">
            {/* quick-suggestion chips */}
            {changesLeft > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => refine(s)}
                    disabled={busy}
                    className="rounded-full border border-theme-chatDivider bg-theme-secondaryBackground px-3 py-1 text-xs text-theme-secondaryText transition-colors hover:border-[#96D7FF] hover:bg-theme-tertiaryBackground disabled:opacity-50"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground px-3 focus-within:border-[#96D7FF]">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && refine()}
                placeholder={
                  changesLeft > 0
                    ? "e.g. rename 'getting-started' to 'welcome'"
                    : "No changes left — create your community"
                }
                disabled={busy || changesLeft <= 0}
                className="w-full bg-transparent py-3 text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder disabled:opacity-60"
              />
              <button
                onClick={() => refine()}
                disabled={busy || changesLeft <= 0 || !input.trim()}
                className="my-1.5 flex-shrink-0 rounded-xl p-2 text-theme-accent transition-colors hover:bg-theme-tertiaryBackground disabled:opacity-40"
              >
                <FiSend size={18} />
              </button>
            </div>
            <button
              onClick={() => setConfirmCommit(true)}
              disabled={busy || channels.length === 0}
              className="w-full rounded-xl bg-gradient-to-r from-[#FBB28B] via-[#96D7FF] to-[#B2EE8F] py-3 text-sm font-bold text-[#202020] shadow-md transition-transform hover:-translate-y-0.5 disabled:translate-y-0 disabled:opacity-50"
            >
              Create {channels.length} channel{channels.length === 1 ? "" : "s"}
              {topicTotal > 0
                ? ` + ${topicTotal} topic${topicTotal === 1 ? "" : "s"}`
                : ""}{" "}
              →
            </button>
          </div>
        )}

        {phase === "done" && (
          <button
            onClick={onDone}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-theme-success py-3 text-sm font-bold text-white shadow-md transition-opacity hover:opacity-90"
          >
            <FiCheckCircle size={16} /> Community created — continue
          </button>
        )}

        {phase === "upgrade" && (
          <button
            onClick={onDone}
            className="w-full rounded-xl border border-theme-chatDivider py-3 text-sm font-semibold text-theme-secondaryText transition-colors hover:bg-theme-tertiaryBackground"
          >
            Continue setup →
          </button>
        )}
      </div>

      <ConfirmDialog
        open={confirmCommit}
        title="Create your community"
        message={`This will create ${channels.length} channel${
          channels.length === 1 ? "" : "s"
        }${
          topicTotal > 0
            ? ` and ${topicTotal} topic${topicTotal === 1 ? "" : "s"}`
            : ""
        }. You can edit them anytime afterwards.`}
        confirmLabel="Create"
        busy={busy}
        onConfirm={commit}
        onClose={() => setConfirmCommit(false)}
      />
    </div>
  );
};

export default BuildCommunityStep;
